/**
 * Single-flight latch for useRecorder.start() (issue #1801).
 *
 * A second start() while the first getUserMedia() is still in flight would run
 * a full second init — a second getUserMedia, AudioContext and MediaRecorder —
 * and whichever promise resolves last overwrites the refs; the loser's stream
 * and AudioContext are orphaned (the mic stays captured, the AudioContext
 * leaks). Both extra entry points race here: double-clicking the mic button and
 * a desktop hold-to-talk rapid Space tap-tap.
 *
 * begin() is called synchronously at the very top of start(), before the first
 * await, so a concurrent caller sees the latch already set and no-ops. end() is
 * called from start()'s finally, so the latch clears on both the success tail
 * and the getUserMedia-reject path — a normal retry stays possible.
 *
 * Pure and framework-free on purpose: the concurrency semantics are executed in
 * node by startGuard.spec.ts, while useRecorder's wiring (begin-before-await,
 * end-in-finally) is asserted statically — the SE never needs a browser.
 */
export interface StartGuard {
  /** Try to enter the start window. Returns false when a start is already in
   *  flight — the caller must no-op. */
  begin(): boolean;
  /** Leave the start window (success or failure). */
  end(): void;
  /** True while a start is in flight (for assertions / debugging). */
  readonly inFlight: boolean;
}

export function createStartGuard(): StartGuard {
  let inFlight = false;
  return {
    begin() {
      if (inFlight) return false;
      inFlight = true;
      return true;
    },
    end() {
      inFlight = false;
    },
    get inFlight() {
      return inFlight;
    },
  };
}
